import { call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import ApiManager from 'network/ApiManager/ApiManager';
import {
  BEAT_REVIEW_FETCHING_OPTION,
  ECG_CHART_UNIT,
  TEN_SEC_STRIP_DETAIL,
  TEN_SEC_STRIP_EDIT,
} from 'constant/ChartEditConst';
import { BPM_SORT_DEFAULT } from 'constant/SortConst';

import { handleNextSelectBpm } from 'util/reduxDuck/HrReviewDuckUtil';
import { selectRecordingTime } from './testResultDuck';
import { validateBeatEditResponse } from 'util/validation/ValidateBeatsEdit';
import { enqueueRequest } from './beatsRequestQueueDuck';
import StatusCode from 'network/StatusCode';

const CONST_BPM_SORT_DEFAULT = BPM_SORT_DEFAULT;
const BUFFER_WAVEFORM_LENGTH =
  BEAT_REVIEW_FETCHING_OPTION.EXTRA_FETCHING_SAMPLE_SIZE;
const HALF_TEN_SEC_WAVEFORM_LENGTH = ECG_CHART_UNIT.HALF_TEN_SEC_WAVEFORM_IDX;
const WAVEFORM_LENGTH_PER_MS = ECG_CHART_UNIT.MS_UNIT_PER_CHART_POINT;

// Selector
const selectEcgTestId = (state) => state.testResultReducer.ecgTestId;
export const selectSidePanelState = (state) => state.hrReviewReducer.sidePanel;
const selectCurrPosition = (state) =>
  state.hrReviewReducer.sidePanel.currPosition;
const selectSelectedBpm = (state) =>
  state.hrReviewReducer.sidePanel.selectedBpm;
const selectHistogram = (state) =>
  state.hrReviewReducer.hrHistogram.data.histogram;
const selectBeatsData = (state) => state.hrReviewReducer.beats.data;
const selectBeatLabelButtonDataList = (state) =>
  state.hrReviewReducer.tenSecStripDetail.beatLabelButtonDataList;

// Actions
const RESET_HR_REVIEW_STATE = 'memo-web/hr-review/RESET_HR_REVIEW_STATE';
const SET_TENSEC_STRIP_DETAIL =
  'memo-web/hr-review/SET_HR_REVIEW_TENSEC_STRIP_DETAIL';
// Get HrReview histogram data
const GET_HR_REVIEW_REQUESTED = 'memo-web/hr-review/GET_HR_REVIEW_REQUESTED';
const GET_HR_REVIEW_SUCCEED = 'memo-web/hr-review/GET_HR_REVIEW_SUCCEED';
const GET_HR_REVIEW_FAILED = 'memo-web/hr-review/GET_HR_REVIEW_FAILED';

const SET_SELECTED_BPM = 'memo-web/hr-review/SET_SELECTED_BPM';
const SET_CURRENT_SORT_ORDER = 'memo-web/hr-review/SET_CURRENT_SORT_ORDER';
const SET_CURRENT_POSITION = 'memo-web/hr-review/SET_CURRENT_POSITION';

// hr-review histogram set-max, set-min
const LIMIT_REQUESTED = 'memo-web/hr-review/LIMIT_REQUESTED';
const LIMIT_SUCCEED = 'memo-web/hr-review/LIMIT_SUCCEED';
const LIMIT_FAILED = 'memo-web/hr-review/LIMIT_FAILED';
// get beats
const GET_BEATS_REQUESTED = 'memo-web/hr-review/GET_BEATS_REQUESTED';
const GET_BEATS_SUCCEED = 'memo-web/hr-review/GET_BEATS_SUCCEED';
const GET_BEATS_FAILED = 'memo-web/hr-review/GET_BEATS_FAILED';

// post beats
const POST_BEATS_REQUESTED = 'memo-web/hr-review/POST_BEATS_REQUESTED';
const POST_BEATS_SUCCEED = 'memo-web/hr-review/POST_BEATS_SUCCEED';
const POST_BEATS_FAILED = 'memo-web/hr-review/POST_BEATS_FAILED';

// patch beats
const PATCH_BEATS_REQUESTED = 'memo-web/hr-review/PATCH_BEATS_REQUESTED';
const PATCH_BEATS_SUCCEED = 'memo-web/hr-review/PATCH_BEATS_SUCCEED';
const PATCH_BEATS_FAILED = 'memo-web/hr-review/PATCH_BEATS_FAILED';

// delete beats
const DELETE_BEATS_REQUESTED = 'memo-web/hr-review/DELETE_BEATS_REQUESTED';
const DELETE_BEATS_SUCCEED = 'memo-web/hr-review/DELETE_BEATS_SUCCEED';
const DELETE_BEATS_FAILED = 'memo-web/hr-review/DELETE_BEATS_FAILED';

// revert
const REVERT_REQUESTED = 'memo-web/hr-review/REVERT_REQUESTED';
const REVERT_SUCCEED = 'memo-web/hr-review/REVERT_SUCCEED';
const REVERT_FAILED = 'memo-web/hr-review/REVERT_FAILED';

// Caliper
const SET_CALIPER_PLOT_LINES = 'memo-web/hr-review/SET_CALIPER_PLOT_LINES';
const SET_IS_CALIPER_MODE = 'memo-web/hr-review/SET_IS_CALIPER_MODE';
const SET_IS_TICK_MARKS_MODE = 'memo-web/hr-review/SET_IS_TICK_MARKS_MODE';

/*
HR Review Data Flow

1. Histogram 데이터 요청
  - dispatch GET_HR_REVIEW_REQUESTED

2. Saga Function(_getHrReview) 에서 API 응답을 받은 후 
  selectedBpm 값이 있는지 여부에 따라 분기

3-1. selectedBpm 값이 없다면, hrMax 값을 selectedBpm 에 할당한 후 
  Beat 목록을 초기화 하기위해 _getBeats Saga Function 을 실행
3-2. selectedBpm 값이 있다면, Beat 목록을 업데이트 하기위해 
  _getBeats Saga Function 을 실행

4. _getBeats Saga Function 에서 신규 Beat 목록을 Global State 에 업데이트 한 후, 
  현재 Position 에 대한 Beat 의 Strip 정보 요청하기 위한 Action Dispatch
  - dispatch SET_CURRENT_POSITION

5. Saga Function(getTenSecStripDetail) 에서 시각화에 필요한 ECG 구간을 요청후 Global State 에 업데이트

그 외
- 정렬 조건(currSortOrder)을 변경하면, Saga Function 으로 _getBeats 실행되고 현재 Position 1 로 변경
- 선택된 BPM(selectedBpm)을 변경하면, Saga Function 으로 _getBeats 실행되고 현재 Position 1 로 변경
 */

const initialState = {
  sidePanel: {
    selectedBpm: null, // 선택된 bpm
    maxPosition: null, // 선택된 Bpm의 Beat의 최대 인덱스(선택된 bpm의 beat수와 같음)
    currSortOrder: CONST_BPM_SORT_DEFAULT, // 선택된 정렬 조건
    currPosition: 1, // 선택된 Bpm의 Beat의 현재 인덱스
  },
  tenSecStripDetail: {
    onsetMs: null,
    terminationMs: null,
    onsetWaveformIdx: null,
    terminationWaveformIdx: null,
    hrAvg: null,
    ecgRaw: [],
    beatLabelButtonDataList: null,
    beatsOrigin: {},
    responseValidationResult: {
      requestAt: null,
      validResult: null,
      editTargetBeatType: null,
    },
    pending: false,
    error: null,
  },
  // HR histogram - data
  hrHistogram: {
    pending: false,
    data: {
      minHr: null,
      maxHr: null,
      avgHr: null,
      histogram: {},
      hrMinBound: null,
      hrMaxBound: null,
      isRevertable: false,
    },
    error: null,
  },
  // HR histogram - limit
  limit: {
    pending: false,
    error: null,
    isUpdate: false,
  },
  // HR histogram - revert
  revert: {
    revertTarget: null,
    afterRevertSelectedBpm: null,
    pending: false,
    error: null,
  },
  // Histogram beats waveformIndex List
  beats: {
    pending: false,
    data: {
      waveformIndex: [],
      beatType: [],
      hr: [],
      bpm: null,
      position: [],
    },
    error: null,
  },
  caliper: {
    caliperPlotLines: [],
    isCaliperMode: false,
    isTickMarksMode: false,
  },
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case RESET_HR_REVIEW_STATE:
      return initialState;
    case SET_TENSEC_STRIP_DETAIL:
      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          pending: false,
          ...action.data,
        },
      };
    // get hr review histogram
    case GET_HR_REVIEW_REQUESTED:
      return {
        ...state,
        hrHistogram: {
          ...state.hrHistogram,
          pending: true,
          error: null,
        },
      };
    case GET_HR_REVIEW_SUCCEED:
      const { result } = action.data;
      let { selectedBpm } = state.sidePanel;

      if (selectedBpm === null) selectedBpm = result.hrMax;

      return {
        ...state,
        sidePanel: {
          ...state.sidePanel,
          selectedBpm: selectedBpm,
          maxPosition: result.histogram[selectedBpm],
        },
        hrHistogram: {
          ...state.hrHistogram,
          data: result,
          pending: false,
          error: null,
        },
      };
    case GET_HR_REVIEW_FAILED:
      return {
        ...state,
        hrHistogram: {
          ...state.hrHistogram,
          pending: false,
          error: action.error,
        },
      };
    case SET_SELECTED_BPM:
      return {
        ...state,
        sidePanel: {
          ...state.sidePanel,
          currPosition: initialState.sidePanel.currPosition,
          selectedBpm: action.selectBpm,
          maxPosition: action.selectBeat,
        },
      };
    case SET_CURRENT_SORT_ORDER:
      return {
        ...state,
        sidePanel: {
          ...state.sidePanel,
          currPosition: initialState.sidePanel.currPosition,
          currSortOrder: action.currSortOrder,
        },
      };
    case SET_CURRENT_POSITION:
      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          pending: true,
          error: null,
        },
        sidePanel: {
          ...state.sidePanel,
          currPosition: action.currPosition,
        },
      };
    // Histogram SET MAX, SET MIN
    case LIMIT_REQUESTED:
      return {
        ...state,
        limit: {
          ...state.limit,
          pending: true,
          error: null,
          isUpdate: false,
        },
      };
    case LIMIT_SUCCEED:
      // Limit 성공시 기존 histogram의 hrMaxBound와 hrMinBound값을 limit response의 hrMaxBound와 hrMinBound와 비교
      // 바뀐 값이 Revert할때 payload로 보낼 값이 된다(되돌리기 이므로 한단계씩 되돌려야하므로)
      const oldHistogram = state.hrHistogram.data;
      const newHrBounds = action.data.result;
      let revertTarget;
      let afterRevertSelectedBpm;

      if (oldHistogram.hrMaxBound !== newHrBounds.hrMaxBound) {
        revertTarget = {
          hrMaxBound: oldHistogram.hrMaxBound,
          hrMinBound: newHrBounds.hrMinBound,
        };
        afterRevertSelectedBpm = oldHistogram.hrMaxBound;
      }

      if (oldHistogram.hrMinBound !== newHrBounds.hrMinBound) {
        revertTarget = {
          hrMinBound: oldHistogram.hrMinBound,
          hrMaxBound: newHrBounds.hrMaxBound,
        };
        afterRevertSelectedBpm = oldHistogram.hrMinBound;
      }

      return {
        ...state,
        limit: {
          ...state.limit,
          pending: false,
          isUpdate: true,
        },
        revert: {
          ...state.revert,
          revertTarget: revertTarget,
          afterRevertSelectedBpm: afterRevertSelectedBpm,
        },
      };
    case LIMIT_FAILED:
      return {
        ...state,
        limit: {
          ...state.limit,
          pending: false,
          error: action.error,
        },
      };
    // Histogram Revert
    case REVERT_REQUESTED:
      return {
        ...state,
        revert: {
          ...state.revert,
          pending: true,
        },
      };
    case REVERT_SUCCEED:
      const revertSelectedBpm = state.revert.afterRevertSelectedBpm;
      return {
        ...state,
        revert: {
          ...state.revert,
          afterRevertSelectedBpm: null,
          pending: false,
        },
        sidePanel: {
          ...state.sidePanel,
          selectedBpm: revertSelectedBpm,
          maxPosition: state.hrHistogram.data.histogram[revertSelectedBpm],
          currPosition: 1,
        },
      };
    case REVERT_FAILED:
      return {
        ...state,
        revert: {
          ...state.revert,
          pending: false,
          error: action.error,
        },
      };
    // get Beats
    case GET_BEATS_REQUESTED:
      return {
        ...state,
        beats: {
          ...state.beats,
          pending: true,
          error: null,
        },
      };
    case GET_BEATS_SUCCEED:
      return {
        ...state,
        sidePanel: {
          ...state.sidePanel,
          maxPosition: action.data.result.maxPosition,
        },
        hrHistogram: {
          ...state.hrHistogram,
          data: {
            ...state.hrHistogram.data,
            histogram: {
              ...state.hrHistogram.data.histogram,
              [state.sidePanel.selectedBpm]: action.data.result.maxPosition,
            },
          },
        },
        beats: {
          ...state.beats,
          data: action.data.result,
          pending: false,
          error: null,
        },
      };
    case GET_BEATS_FAILED:
      return {
        ...state,
        beats: {
          ...state.beats,
          pending: false,
          error: action.error,
        },
      };
    // POST Beats - 비트추가
    case POST_BEATS_REQUESTED:
      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          pending: true,
          error: null,
        },
        beats: {
          ...state.beats,
          pending: true,
          error: null,
        },
      };
    case POST_BEATS_SUCCEED:
      let newBeatLabelButtonDataListAfterPostBeats;
      (function () {
        const {
          data: { result: apiResResult },
          tabType,
        } = action;
        const { beatLabelButtonDataList, onsetWaveformIdx } =
          state.tenSecStripDetail;
        if (
          Array.isArray(apiResResult.waveformIndex) &&
          apiResResult.waveformIndex.length > 0
        ) {
          const editTargetWaveformIndex =
            apiResResult.waveformIndex[0] - onsetWaveformIdx;
          const editTargetBeatType = apiResResult.beatType[0];
          const nextIndexOfAddBeat = beatLabelButtonDataList.findIndex(
            (v) => v.xAxisPoint > editTargetWaveformIndex
          );
          beatLabelButtonDataList.splice(nextIndexOfAddBeat, 0, {
            xAxisPoint: editTargetWaveformIndex,
            beatType: editTargetBeatType,
            title: TEN_SEC_STRIP_EDIT.BEAT_TYPE[editTargetBeatType],
            color: TEN_SEC_STRIP_EDIT.BEAT_COLOR_TYPE[editTargetBeatType],
            isSelected: false,
          });
        }
        newBeatLabelButtonDataListAfterPostBeats = beatLabelButtonDataList;
      })();

      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          beatLabelButtonDataList: [
            ...newBeatLabelButtonDataListAfterPostBeats,
          ],
          responseValidationResult: action.responseValidationResult,
          pending: false,
          error: null,
        },
        beats: {
          ...state.beats,
          pending: false,
          error: null,
        },
      };
    case POST_BEATS_FAILED:
      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          pending: false,
          error: action.error,
        },
        beats: {
          ...state.beats,
          pending: false,
          error: null,
        },
      };
    // PATCH Beats
    case PATCH_BEATS_REQUESTED:
      return {
        ...state,
        beats: {
          ...state.beats,
          pending: true,
          error: null,
        },
      };
    case PATCH_BEATS_SUCCEED:
      // 업데이트 성공시 get 재호출
      // 10s strip detail에서 업데이트 하는 경우만
      let newBeatLabelButtonDataListAfterPatchBeats;
      let updateBeatLabelButtonDataList = false;

      (function () {
        const {
          data: { result: apiResResult },
          tabType,
        } = action;

        if (tabType === TEN_SEC_STRIP_DETAIL.TAB.ARRHYTHMIA_CONTEXTMENU) return;

        updateBeatLabelButtonDataList = true;
        const { beatLabelButtonDataList, onsetWaveformIdx } =
          state.tenSecStripDetail;

        for (let i in apiResResult.waveformIndex) {
          newBeatLabelButtonDataListAfterPatchBeats =
            beatLabelButtonDataList.map((v) => {
              if (
                v.xAxisPoint ===
                apiResResult.waveformIndex[i] - onsetWaveformIdx
              ) {
                v.isSelected = false;
                v.beatType = apiResResult.beatType[i];
                v.title = TEN_SEC_STRIP_EDIT.BEAT_TYPE[v.beatType];
                v.color = TEN_SEC_STRIP_EDIT.BEAT_COLOR_TYPE[v.beatType];
                return v;
              }
              return v;
            });
        }
      })();

      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          beatLabelButtonDataList: updateBeatLabelButtonDataList
            ? [...newBeatLabelButtonDataListAfterPatchBeats]
            : state.tenSecStripDetail.beatLabelButtonDataList,
          responseValidationResult: action.responseValidationResult,
          pending: false,
          error: null,
        },
        beats: {
          ...state.beats,
          pending: false,
          error: null,
        },
      };
    case PATCH_BEATS_FAILED:
      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          pending: false,
          error: null,
        },
        beats: {
          ...state.beats,
          pending: false,
          error: null,
        },
      };
    // DELETE Beats
    case DELETE_BEATS_REQUESTED:
      return {
        ...state,
        beats: {
          ...state.beats,
          pending: true,
          error: null,
        },
      };
    case DELETE_BEATS_SUCCEED:
      let newBeatLabelButtonDataListAfterDeleteBeats;

      (function () {
        const { reqBody } = action;
        const { beatLabelButtonDataList, onsetWaveformIdx } =
          state.tenSecStripDetail;

        const editTargetWaveformIndexList = reqBody.waveformIndexes.map(
          (v) => v - onsetWaveformIdx
        );
        const filteredBeatLabelButtonDataList = beatLabelButtonDataList.filter(
          (beatLabelButtonData) =>
            !editTargetWaveformIndexList.includes(
              beatLabelButtonData.xAxisPoint
            )
        );
        newBeatLabelButtonDataListAfterDeleteBeats =
          filteredBeatLabelButtonDataList;
      })();

      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          beatLabelButtonDataList: [
            ...newBeatLabelButtonDataListAfterDeleteBeats,
          ],
          pending: false,
          error: null,
        },
        beats: {
          ...state.beats,
          pending: false,
          error: null,
        },
      };
    case DELETE_BEATS_FAILED:
      return {
        ...state,
        tenSecStripDetail: {
          ...state.tenSecStripDetail,
          pending: false,
          error: null,
        },
        beats: {
          ...state.beats,
          pending: false,
          error: null,
        },
      };
    case SET_CALIPER_PLOT_LINES:
      return {
        ...state,
        caliper: {
          ...state.caliper,
          caliperPlotLines: action.caliperPlotLines,
        },
      };
    case SET_IS_CALIPER_MODE:
      return {
        ...state,
        caliper: {
          ...state.caliper,
          isCaliperMode: action.isCaliperMode,
        },
      };
    case SET_IS_TICK_MARKS_MODE:
      return {
        ...state,
        caliper: {
          ...state.caliper,
          isTickMarksMode: action.isTickMarksMode,
        },
      };
    default:
      return state;
  }
}

// Action Creators
// Set 10s Strip Detail
export function resetHrReviewState() {
  return { type: RESET_HR_REVIEW_STATE };
}

// Set 10s Strip Detail
function setTenSecStripDetail(data) {
  return { type: SET_TENSEC_STRIP_DETAIL, data };
}

// Get HR Histogram
export function getHrReviewRequested() {
  return { type: GET_HR_REVIEW_REQUESTED };
}
export function getHrReviewSucceed(data) {
  return { type: GET_HR_REVIEW_SUCCEED, data };
}
function getHrReviewFailed(error) {
  return { type: GET_HR_REVIEW_FAILED, error };
}

// hr review histogram set max/min limit
export function limitRequested(ecgTestId, reqBody) {
  return {
    type: LIMIT_REQUESTED,
    ecgTestId,
    reqBody,
  };
}
export function limitSucceed(data) {
  return {
    type: LIMIT_SUCCEED,
    data,
  };
}
export function limitFailed(error) {
  return {
    type: LIMIT_FAILED,
    error,
  };
}

// hr review histogram max/min revert
export function revertRequested(reqBody) {
  return {
    type: REVERT_REQUESTED,
    reqBody,
  };
}
export function revertSucceed(data) {
  return {
    type: REVERT_SUCCEED,
    data,
  };
}
export function revertFailed(error) {
  return {
    type: REVERT_FAILED,
    error,
  };
}

// get beats
export function getBeatsRequested(bpm) {
  return {
    type: GET_BEATS_REQUESTED,
    bpm,
  };
}
function getBeatsSucceed(data) {
  return {
    type: GET_BEATS_SUCCEED,
    data,
  };
}
function getBeatsFailed(error) {
  return {
    type: GET_BEATS_FAILED,
    error,
  };
}

// post beats
export function postBeatsRequested(
  reqBody,
  onsetWaveformIndex,
  terminationWaveformIndex,
  suffix,
  tabType,
  selectedBeatBtnWaveformIndexList
) {
  return {
    type: POST_BEATS_REQUESTED,
    reqBody,
    onsetWaveformIndex,
    terminationWaveformIndex,
    suffix,
    tabType,
    selectedBeatBtnWaveformIndexList,
  };
}
function postBeatsSucceed(data, responseValidationResult) {
  return {
    type: POST_BEATS_SUCCEED,
    data,
    responseValidationResult,
  };
}
function postBeatsFailed(error) {
  return {
    type: POST_BEATS_FAILED,
    error,
  };
}

// patch beats
export function patchBeatsRequested(
  reqBody,
  onsetWaveformIndex,
  terminationWaveformIndex,
  suffix,
  tabType,
  selectedBeatBtnWaveformIndexList
) {
  return {
    type: PATCH_BEATS_REQUESTED,
    reqBody,
    onsetWaveformIndex,
    terminationWaveformIndex,
    suffix,
    tabType,
    selectedBeatBtnWaveformIndexList,
  };
}
function patchBeatsSucceed(data, tabType, responseValidationResult) {
  return {
    type: PATCH_BEATS_SUCCEED,
    data,
    tabType,
    responseValidationResult,
  };
}
function patchBeatsFailed(error) {
  return {
    type: PATCH_BEATS_FAILED,
    error,
  };
}

// delete beats
export function deleteBeatsRequested(
  reqBody,
  onsetWaveformIndex,
  terminationWaveformIndex,
  suffix,
  tabType,
  selectedBeatBtnWaveformIndexList
) {
  return {
    type: DELETE_BEATS_REQUESTED,
    reqBody,
    onsetWaveformIndex,
    terminationWaveformIndex,
    suffix,
    tabType,
    selectedBeatBtnWaveformIndexList,
  };
}
function deleteBeatsSucceed(reqBody) {
  return {
    type: DELETE_BEATS_SUCCEED,
    reqBody,
  };
}
function deleteBeatsFailed(error) {
  return {
    type: DELETE_BEATS_FAILED,
    error,
  };
}

// set sidePanel state
export function setSelectedBpm(selectBpm, selectBeat) {
  return {
    type: SET_SELECTED_BPM,
    selectBpm,
    selectBeat,
  };
}
export function setCurrentSortOrder(currSortOrder) {
  return { type: SET_CURRENT_SORT_ORDER, currSortOrder };
}
export function setCurrentPosition(currPosition) {
  return { type: SET_CURRENT_POSITION, currPosition };
}

// Caliper
export function setCaliperPlotLines(caliperPlotLines) {
  return { type: SET_CALIPER_PLOT_LINES, caliperPlotLines };
}
export function setIsCaliperMode(isCaliperMode) {
  return { type: SET_IS_CALIPER_MODE, isCaliperMode };
}
export function setIsTickMarksMode(isTickMarksMode) {
  return { type: SET_IS_TICK_MARKS_MODE, isTickMarksMode };
}

// Saga functions
function* _getHrReview(action) {
  try {
    const ecgTestId = yield select(selectEcgTestId);
    const apiEndpoint = ApiManager.getHrHistogram;
    const { data } = yield call(apiEndpoint, {
      ecgTestId,
    });

    yield put(getHrReviewSucceed(data));

    const { selectedBpm } = yield select(selectSidePanelState);
    if (selectedBpm) {
      yield put(getBeatsRequested());
    } else {
      const { histogram, hrMax } = data.result;
      yield put(setSelectedBpm(hrMax, histogram[hrMax]));
    }
  } catch (error) {
    yield put(getHrReviewFailed(error));
  }
}

function* _limit(action) {
  try {
    const { data } = yield call(
      ApiManager.limit,
      action.ecgTestId,
      action.reqBody
    );

    yield put(limitSucceed(data));
  } catch (error) {
    yield put(limitFailed(error));
  }
}

function* _revert(action) {
  try {
    const ecgTestId = yield select(selectEcgTestId);
    const { data, status } = yield call(
      ApiManager.revert,
      ecgTestId,
      action.reqBody
    );
    // 성공시 getHistogram
    if (status === 200) {
      // 해당 req 성공 이후 revert한 결과에 따라서 selectedBpm을 변경해준다
      yield put(getHrReviewRequested());
    }

    yield put(revertSucceed(data));
  } catch (error) {
    yield put(revertFailed(error));
  }
}

/** 선택된 BPM 과 Position 에 대한 ECG 데이터(with beats) 를 요청 */
function* getTenSecStripDetail(action) {
  try {
    const ecgTestId = yield select(selectEcgTestId);
    const _selectBeatsData = yield select(selectBeatsData);
    const { recordingStartMs, recordingEndMs } = yield select(
      selectRecordingTime
    );
    const selectedIndex =
      _selectBeatsData.waveformIndex[action.currPosition - 1];

    const onsetWaveformIndex = selectedIndex - HALF_TEN_SEC_WAVEFORM_LENGTH;
    const terminationWaveformIndex =
      selectedIndex + HALF_TEN_SEC_WAVEFORM_LENGTH;

    // ECG Test 측정 기간을 벗어난 조회 방지용
    const queryOnsetMs = Math.max(
      recordingStartMs +
        (onsetWaveformIndex - BUFFER_WAVEFORM_LENGTH) * WAVEFORM_LENGTH_PER_MS,
      recordingStartMs
    );
    const queryTerminationMs = Math.min(
      recordingStartMs +
        (terminationWaveformIndex + BUFFER_WAVEFORM_LENGTH) *
          WAVEFORM_LENGTH_PER_MS,
      recordingEndMs
    );

    const {
      data: { results },
    } = yield call(ApiManager.getRawEcg, {
      ecgTestId,
      onsetMs: queryOnsetMs,
      terminationMs: queryTerminationMs,
      withBeat: true,
    });

    const subEcgRaw = rawEcgData(
      onsetWaveformIndex,
      terminationWaveformIndex,
      results
    );

    const beats = {
      waveformIndex: results[0].beats.waveformIndex.concat(
        results[1]?.beats.waveformIndex || []
      ),
      beatType: results[0].beats.beatType.concat(
        results[1]?.beats.beatType || []
      ),
      hr: results[0].beats.hr.concat(results[1]?.beats.hr || []),
    };
    const selectedBeatIndex = beats.waveformIndex.findIndex(
      (value) => value === selectedIndex
    );
    let prevSelectedIndex = null;
    if (selectedBeatIndex > 0) {
      prevSelectedIndex = beats.waveformIndex[selectedBeatIndex - 1];
    }
    const rRIntervalSecond = prevSelectedIndex
      ? Number.parseFloat(
          ((selectedIndex - prevSelectedIndex) * 4) / 1000
        ).toFixed(3)
      : null;

    const data = {
      ...tenSecStripDataHandler(
        onsetWaveformIndex,
        terminationWaveformIndex,
        beats
      ),
      ecgRaw: subEcgRaw,
      onsetWaveformIdx: onsetWaveformIndex,
      terminationWaveformIdx: terminationWaveformIndex,
      onsetMs: recordingStartMs + onsetWaveformIndex * 4,
      terminationMs: recordingStartMs + terminationWaveformIndex * 4,
      rRIntervalSecond,
    };
    yield put(setTenSecStripDetail(data));
  } catch (error) {
    console.error(error);
  }

  function tenSecStripDataHandler(
    onsetWaveformIndex,
    terminationWaveformIndex,
    beats
  ) {
    const { hr, waveformIndex } = beats;
    const hrArray = hr
      .slice(
        waveformIndex.findIndex((v) => v >= onsetWaveformIndex),
        waveformIndex.findLastIndex((v) => v <= terminationWaveformIndex) + 1
      )
      .filter(Boolean);
    const sumHr = hrArray.reduce((acc, cur) => acc + cur, 0);
    const tenSecStripHrAvg = Math.floor((sumHr / hrArray.length) * 10) / 10;
    const beatLabelButtonDataList = [];
    const beatType = TEN_SEC_STRIP_EDIT.BEAT_TYPE;
    const beatColorType = TEN_SEC_STRIP_EDIT.BEAT_COLOR_TYPE;
    const waveformIndexList = waveformIndex
      .slice(
        waveformIndex.findIndex((v) => v >= onsetWaveformIndex),
        waveformIndex.findLastIndex((v) => v <= terminationWaveformIndex) + 1
      )
      .map((waveformIdx) => waveformIdx - onsetWaveformIndex);
    const beatTypeList = beats.beatType.slice(
      waveformIndex.findIndex((v) => v >= onsetWaveformIndex),
      waveformIndex.findLastIndex((v) => v <= terminationWaveformIndex) + 1
    );
    for (let index in waveformIndexList) {
      beatLabelButtonDataList.push({
        xAxisPoint: waveformIndexList[index],
        isSelected:
          waveformIndexList[index] ===
          beats.waveformIndex[0] - onsetWaveformIndex,
        beatType: beatTypeList[index],
        title: beatType[beatTypeList[index]],
        color: beatColorType[beatTypeList[index]],
        isEventReview: '',
      });
    }

    const result = {
      hrAvg: tenSecStripHrAvg,
      beatLabelButtonDataList,
      beatsOrigin: beats,
    };
    return result;
  }
  function rawEcgData(onsetWaveformIndex, terminationWaveformIndex, results) {
    const responsedOnsetWaveformIndex = results[0].onsetWaveformIndex;
    const sliceStartIndex = onsetWaveformIndex - responsedOnsetWaveformIndex;
    const sliceEndIndex =
      terminationWaveformIndex - responsedOnsetWaveformIndex;
    if (results.length === 1) {
      // Hr Review 검사 시작지점 검증
      if (onsetWaveformIndex < 0) {
        return [
          ...Array.from(
            {
              length: Math.abs(onsetWaveformIndex),
            },
            () => 0
          ),
        ].concat(results[0].ecgData);
      }

      return results[0].ecgData.slice(sliceStartIndex, sliceEndIndex);
    } else {
      const entireEcgDataArray = results[0].ecgData.concat(results[1].ecgData);
      return entireEcgDataArray.slice(sliceStartIndex, sliceEndIndex);
    }
  }
}

function* _getBeats(action) {
  try {
    const ecgTestId = yield select(selectEcgTestId);
    const { selectedBpm, currSortOrder, currPosition } = yield select(
      selectSidePanelState
    );

    const bpm = action.bpm ?? selectedBpm;
    if (!bpm) throw new Error('_getBeats Error: bpm 값 없음');

    const { data } = yield call(ApiManager.getBeatsFilterBpm, {
      ecgTestId,
      bpm: bpm,
      orderBy: currSortOrder.queryOrderBy,
      ascending: currSortOrder.ascending,
    });

    yield put(getBeatsSucceed(data));
    yield put(setCurrentPosition(currPosition));
  } catch (error) {
    yield put(getBeatsFailed(error));
  }
}

function* _postBeats(action) {
  try {
    const _previousSelectedBpm = yield select(selectSelectedBpm);
    const _selectHistogram = yield select(selectHistogram);
    const ecgTestId = yield select(selectEcgTestId);
    const _selectBeatLabelButtonDataList = yield select(
      selectBeatLabelButtonDataList
    );
    const _selectCurrPosition = yield select(selectCurrPosition);

    const { selectedBeatBtnWaveformIndexList } = action;
    const { nextSelectBpm, nextPosition } = handleNextSelectBpm({
      _selectHistogram,
      _previousSelectedBpm,
      _selectBeatLabelButtonDataList,
      selectedBeatBtnWaveformIndexList,
      _selectCurrPosition,
    });

    const requestAt = new Date().getTime();
    const requestStatement = {
      requestType: action.suffix,
      ecgTestId: ecgTestId,
      reqBody: action.reqBody,
    };

    yield put(
      enqueueRequest({
        requestStatement,
        succeedCallback,
        failedCallback,
      })
    );

    function* succeedCallback({ data }) {
      yield put(
        postBeatsSucceed(data, {
          requestAt,
          validResult: validateBeatEditResponse(action.reqBody, data.result),
          editTargetBeatType: action.reqBody.beatType,
        })
      );
      yield put(getHrReviewRequested());

      // 선택된 bpm의 maxPosition이 1이고, 비트업데이트로 인해 선택된 bpm이 변경되면 다음 bpm을 조회
      if (nextSelectBpm === _previousSelectedBpm) {
        if (nextPosition !== _selectCurrPosition) {
          yield put(setCurrentPosition(nextPosition));
        }
        yield put(getBeatsRequested(nextSelectBpm));
      } else {
        yield put(
          setSelectedBpm(Number(nextSelectBpm), _selectHistogram[nextSelectBpm])
        );
      }
    }
    function* failedCallback(error) {
      yield put(postBeatsFailed(error));
    }
  } catch (error) {
    yield put(postBeatsFailed(error));
  }
}

function* _patchBeats(action) {
  try {
    const _previousSelectedBpm = yield select(selectSelectedBpm);
    const _selectHistogram = yield select(selectHistogram);
    const ecgTestId = yield select(selectEcgTestId);
    const _selectBeatLabelButtonDataList = yield select(
      selectBeatLabelButtonDataList
    );
    const _selectCurrPosition = yield select(selectCurrPosition);

    const { reqBody, suffix, tabType, selectedBeatBtnWaveformIndexList } =
      action;
    const updateBeatType = reqBody?.beatType;
    const { nextSelectBpm, nextPosition } = handleNextSelectBpm({
      _selectHistogram,
      _previousSelectedBpm,
      _selectBeatLabelButtonDataList,
      selectedBeatBtnWaveformIndexList,
      _selectCurrPosition,
      updateBeatType,
    });

    const requestAt = new Date().getTime();
    const requestStatement = {
      requestType: suffix,
      ecgTestId: ecgTestId,
      reqBody: reqBody,
    };

    yield put(
      enqueueRequest({
        requestStatement,
        succeedCallback,
        failedCallback,
      })
    );

    function* succeedCallback({ data }) {
      yield put(
        patchBeatsSucceed(data, tabType, {
          requestAt,
          validResult: validateBeatEditResponse(reqBody, data.result),
          editTargetBeatType: reqBody.beatType,
        })
      );
      yield put(getHrReviewRequested());

      // 선택된 bpm의 maxPosition이 1이고, 비트업데이트로 인해 선택된 bpm이 변경되면 다음 bpm을 조회
      if (nextSelectBpm === _previousSelectedBpm) {
        if (nextPosition !== _selectCurrPosition) {
          yield put(setCurrentPosition(nextPosition));
        }
        yield put(getBeatsRequested(nextSelectBpm));
      } else {
        yield put(
          setSelectedBpm(Number(nextSelectBpm), _selectHistogram[nextSelectBpm])
        );
      }
    }
    function* failedCallback(error) {
      yield put(patchBeatsFailed(error));
    }
  } catch (error) {
    console.error('error: ', error);
    yield put(patchBeatsFailed(error));
  }
}

function* _deleteBeats(action) {
  try {
    const _previousSelectedBpm = yield select(selectSelectedBpm);
    const _selectHistogram = yield select(selectHistogram);
    const ecgTestId = yield select(selectEcgTestId);
    const _selectBeatLabelButtonDataList = yield select(
      selectBeatLabelButtonDataList
    );
    const _selectCurrPosition = yield select(selectCurrPosition);

    const { reqBody, suffix, selectedBeatBtnWaveformIndexList } = action;
    const { nextSelectBpm, nextPosition } = handleNextSelectBpm({
      _selectHistogram,
      _previousSelectedBpm,
      _selectBeatLabelButtonDataList,
      selectedBeatBtnWaveformIndexList,
      _selectCurrPosition,
    });
    const requestStatement = {
      requestType: suffix,
      ecgTestId: ecgTestId,
      reqBody: reqBody,
    };

    yield put(
      enqueueRequest({
        requestStatement,
        succeedCallback,
        failedCallback,
      })
    );

    function* succeedCallback({ status }) {
      if (status !== StatusCode.NO_CONTENT) {
        yield put(deleteBeatsFailed({}));
        return;
      }

      yield put(deleteBeatsSucceed(reqBody));
      yield put(getHrReviewRequested());

      // 선택된 bpm의 maxPosition이 1이고, 비트업데이트로 인해 선택된 bpm이 변경되면 다음 bpm을 조회
      if (nextSelectBpm === _previousSelectedBpm) {
        if (nextPosition !== _selectCurrPosition) {
          yield put(setCurrentPosition(nextPosition));
        }
        yield put(getBeatsRequested(nextSelectBpm));
      } else {
        yield put(
          setSelectedBpm(Number(nextSelectBpm), _selectHistogram[nextSelectBpm])
        );
      }
    }
    function* failedCallback(error) {
      yield put(deleteBeatsFailed(error));
    }
  } catch (error) {
    yield put(deleteBeatsFailed(error));
  }
}

// Saga
export function* saga() {
  // :: TAB :: hr review tab
  yield takeLatest(GET_HR_REVIEW_REQUESTED, _getHrReview);
  yield takeLatest(LIMIT_REQUESTED, _limit);
  yield takeLatest(REVERT_REQUESTED, _revert);
  yield takeLatest(SET_SELECTED_BPM, _getBeats);
  yield takeLatest(SET_CURRENT_SORT_ORDER, _getBeats);
  yield takeLatest(SET_CURRENT_POSITION, getTenSecStripDetail);
  yield takeLatest(GET_BEATS_REQUESTED, _getBeats);

  // :: 10s strip detail - beat edit ::
  yield takeEvery(POST_BEATS_REQUESTED, _postBeats); // create beat
  yield takeEvery(PATCH_BEATS_REQUESTED, _patchBeats); // modify beat
  yield takeEvery(DELETE_BEATS_REQUESTED, _deleteBeats); // delete beat
}
